import { useEffect, useMemo, useState } from 'react'
import { api, type AbbruecheBild, type Auswertung } from '../api'
import { anzahl, euro, menge } from '../format'
import { Auswertungsraster } from '../komponenten/Auswertungsraster'
import { Balken } from '../komponenten/Balken'
import { stundenLuecken } from '../komponenten/diagramm'
import { Tipp } from '../komponenten/Hinweis'
import { Karte } from '../komponenten/Karte'

const ARTIKELHINWEIS = <>
  Abbrüche zählen nicht zum Umsatz. Zugeordnet wird die Bestellung, die an
  diesem Tisch höchstens eine Minute vor dem Abbruch erfasst wurde. Mehrere
  Bestellungen derselben Sekunde werden gemeinsam gezeigt; ihr Zusammenhang
  ist plausibel, aber nicht durch eine eigene Kennung der Kasse bewiesen. Die
  Beträge sind der Wert der Buchungen vor dem Abbruch, kein entgangener Umsatz:
  Ein Teil wurde danach auf einem anderen Tisch gebucht, ein Teil war nie eine
  Bestellung.
</>

const ARTIKELTIPPS = {
  eindeutig: 'Unmittelbar vor dem Abbruch wurde an diesem Tisch bestellt. Die Artikel dieser Bestellung sind die abgebrochenen.',
  block: 'An diesem Tisch wurden mehrere Bestellungen in derselben Sekunde erfasst; die Kasse zerlegt eine Buchung manchmal in mehrere. Wir zählen alle dazu – das ist plausibel, aber nicht bewiesen, deshalb steht es getrennt.',
  ohneZeitnah: 'Die letzte Bestellung an diesem Tisch liegt mehr als eine Minute zurück, meist mehrere Stunden. Hier hat jemand einen Tisch abgebrochen, an dem er selbst gerade nichts gebucht hat; welche Artikel betroffen waren, lässt sich nicht sagen. Ob das üblich ist – etwa beim Schichtwechsel –, weiß nur das Haus.',
  ohne: 'An diesem Tisch gibt es gar keine Bestellung, zu der der Abbruch gehören könnte.',
}

export function Abbrueche({ von, bis, betriebe, audit = false }: {
  von: string; bis: string; betriebe: string[]; audit?: boolean
}) {
  const [d, setD] = useState<AbbruecheBild | null>(null)
  const [kacheln, setKacheln] = useState<Auswertung[]>([])
  const [fehler, setFehler] = useState<string | null>(null)

  useEffect(() => {
    let weg = false
    setFehler(null)
    api.auswertungen('abbrueche').then(async (alle) => {
      const sichtbar = audit ? alle : alle.filter((k) => k.werkzeug === 'REPORTING')
      const aktiv = sichtbar.filter((k) => k.aktiv).map((k) => k.schluessel)
      const bild = await api.abbrueche(von, bis, betriebe, aktiv, audit)
      if (!weg) { setKacheln(sichtbar); setD(bild) }
    }).catch((e) => { if (!weg) setFehler(e.message) })
    return () => { weg = true }
  }, [von, bis, betriebe.join(','), audit])

  const summen = useMemo(() => ({
    vorgaenge: d?.verlauf?.reduce((s, z) => s + z.vorgaenge, 0) ?? 0,
    kreise: d?.verlauf?.reduce((s, z) => s + z.kreise, 0) ?? 0,
  }), [d])

  const stunden = stundenLuecken(d?.stunden ?? [],
    (h) => ({ stunde: h, vorgaenge: 0, kreise: 0 }))
  const etikettSchritt = stunden.length > 18 ? 3 : stunden.length > 12 ? 2 : 1

  if (fehler) return <div className="fehler">{fehler}</div>
  if (!d) return <div className="laedt">Wird geladen …</div>

  const inhalt = {
    'abbrueche.verlauf': () => (
      <Karte titel="Belegabbrüche im Verlauf" neben="abgebrochene Vorgänge aus dem Kassenarchiv">
        {summen.vorgaenge ? <>
          <table><tbody>
            <tr><td>Abbruchvorgänge</td><td className="r zahl">{anzahl(summen.vorgaenge)}</td></tr>
            <tr><td>Betroffene Abrechnungskreise</td><td className="r zahl">{anzahl(summen.kreise)}</td></tr>
          </tbody></table>
          <Balken
            werte={d.verlauf!.map((z, i) => ({
              schluessel: z.tag,
              etikett: i % 5 === 0 || i === d.verlauf!.length - 1
                ? `${z.tag.slice(8)}.${z.tag.slice(5, 7)}.` : '',
              wert: z.vorgaenge,
              neben: anzahl(z.kreise),
            }))}
            formatiere={anzahl} formatiereAchse={anzahl}
            wertName="Abbruchvorgänge" nebenName="Abrechnungskreise" />
        </> : <div className="leer">Im gewählten Zeitraum gibt es keine Belegabbrüche.</div>}
      </Karte>
    ),
    'abbrueche.uhrzeit': () => (
      <Karte titel="Belegabbrüche nach Uhrzeit" neben="Ortszeit des Standorts">
        {d.stunden?.length ? <Balken
          werte={stunden.map((z, i) => ({
            schluessel: String(z.stunde),
            etikett: i % etikettSchritt === 0 || i === stunden.length - 1
              ? String(z.stunde).padStart(2, '0') : '',
            wert: z.vorgaenge,
            neben: anzahl(z.kreise),
          }))}
          formatiere={anzahl} formatiereAchse={anzahl}
          wertName="Abbruchvorgänge" nebenName="Abrechnungskreise"
          xAchsenTitel="Uhr" />
          : <div className="leer">Im gewählten Zeitraum gibt es keine Uhrzeiten zu Belegabbrüchen.</div>}
      </Karte>
    ),
    'abbrueche.artikel': () => (
      <Karte titel="Artikel vor dem Belegabbruch"
             neben={ARTIKELHINWEIS}>
        {d.artikel ? <>
          <table><tbody>
            <tr><td>Artikel zugeordnet <Tipp text={ARTIKELTIPPS.eindeutig} /></td>
              <td className="r zahl">{anzahl(d.artikel.eindeutig)}</td></tr>
            <tr><td>Gleichzeitig gebucht <Tipp text={ARTIKELTIPPS.block} /></td>
              <td className="r zahl">{anzahl(d.artikel.block)}</td></tr>
            <tr><td>Abbruch ohne Buchung davor
              {' '}<Tipp text={ARTIKELTIPPS.ohneZeitnah} /></td>
              <td className="r zahl">{anzahl(d.artikel.ohne_zeitnahe_buchung)}</td></tr>
            <tr><td>Kein Abbruch ohne Bestellung
              {' '}<Tipp text={ARTIKELTIPPS.ohne} /></td>
              <td className="r zahl">{anzahl(d.artikel.ohne)}</td></tr>
          </tbody></table>
          {d.artikel.artikel.length ? <table><thead><tr>
            <th>Artikel</th><th className="r">Abbrüche</th>
            <th className="r">Menge</th><th className="r">Betrag</th>
          </tr></thead><tbody>{d.artikel.artikel.map((z) => (
            <tr key={z.artikel}><td>{z.artikel}</td>
              <td className="r zahl">{anzahl(z.abbrueche)}</td>
              <td className="r zahl">{menge(z.menge)}</td>
              <td className="r zahl">{euro(z.summe)}</td></tr>
          ))}</tbody></table>
            : <div className="leer">Die zugeordneten Abbrüche enthalten keine Artikel.</div>}
        </> : <div className="leer">Im gewählten Zeitraum gibt es keine Belegabbrüche.</div>}
      </Karte>
    ),
    'abbrueche.mitarbeiter': () => (
      <Karte titel="Belegabbrüche je Person" neben="nur im Audit, ohne Namen">
        {!d.mit_personen ? (
          <div className="leer">Die Bedienerdimension ist für diesen Mandanten nicht eingeschaltet.</div>
        ) : d.mitarbeiter?.length ? (
          <table>
            <thead><tr><th>Bedienerkennung</th><th className="r">Vorgänge</th><th className="r">Anteil</th></tr></thead>
            <tbody>{d.mitarbeiter.map((z) => (
              <tr key={z.person_id}><td>{z.kennung}</td>
                <td className="r zahl">{anzahl(z.vorgaenge)}</td>
                <td className="r zahl">{z.anteil.toLocaleString('de-DE', { maximumFractionDigits: 1 })} %</td></tr>
            ))}</tbody>
          </table>
        ) : <div className="leer">Im gewählten Zeitraum gibt es keine Belegabbrüche je Person.</div>}
      </Karte>
    ),
  }

  return <>
    <div className="punkt" style={{ marginBottom: 14 }}>
      <b>Wie die Artikel zugeordnet werden</b>
      <div className="dezent" style={{ marginTop: 4 }}>
        Zeitnahe Bestellungen werden zugeordnet; gleichzeitige Buchungsblöcke
        und Abbrüche ohne zeitnahe Buchung bleiben getrennt ausgewiesen.
      </div>
    </div>
    <Auswertungsraster kacheln={kacheln} inhalt={inhalt} />
  </>
}
